import { createPostGraphqlRequest } from '@tv2/lib-core/request';
import { defer, EMPTY, Observable, repeatWhen } from 'rxjs';
import { catchError, delay, map, share, switchMap, takeWhile } from 'rxjs/operators';
import { PlayUnifiedLiveEpg, PlayUnifiedLiveEpgQuery } from './__generated__/Epg';
import { PollResult } from './types';
import { startOverEpgPoll } from './StartOver';
import { UseTrackingProps } from '../../plugins/Tracking';
import { pollHeaders } from '../helpers';
import type { Media } from '../../types';

const minute = 60 * 1000;
const liveEpgQuery = {
  operationName: 'play_unified_live_epg',
  query: PlayUnifiedLiveEpg,
};

/**
 * Getter function for extracting `epgentries` information from `epg` data.
 * @param   epg - Object returned from graphql endpoint.
 * @returns Array of epgEntry nodes (empty if nothing was found).
 */
export function epgEntries(epg: PlayUnifiedLiveEpgQuery) {
  // eslint-disable-next-line no-underscore-dangle
  if (epg?.entity?.__typename !== 'Broadcast') return [];

  return epg?.entity?.epgEntries?.nodes ?? [];
}
/**
 * Fetches EPG data from the `endpoint` given an entity `guid`.
 * @param   endpoint - URL of the graphql endpoint.
 * @param   guid     - Entity guid
 * @param includeStartOverInformation
 * @returns Promise of graphql response.
 */
export function query<T>(
  options: UseTrackingProps['epgOptions'],
  guid: string,
  includeStartOverInformation: boolean
) {
  return createPostGraphqlRequest<T>(
    options.endpoint,
    liveEpgQuery.query,
    { guid, includeStartOverInformation },
    liveEpgQuery.operationName,
    pollHeaders(options.clientPlatform)
  );
}

/**
 * Given an observable of entity guids, returns an observable that, upon subscription,
 * continuously requests EPG data (every 5 minutes). In case the entity is not a station,
 * the polling will be stopped and nothing is emitted. The returned observable is shared.
 * If the media is a start over, the start over epg query will be used instead.
 * @param   media$    - Observable of entity guids.
 * @param   endpoint - URL of the graphql endpoint.
 * @param   includeStartOverInformation - Whether to include start over information in the query.
 * @returns Observable emitting epg entries and tracking information for station entities.
 */
export function poll$(
  media$: Observable<Media>,
  options: UseTrackingProps['epgOptions'],
  includeStartOverInformation: boolean = false
): Observable<PollResult> {
  return media$.pipe(
    switchMap(media => {
      if (media.metadata.isStartOver) {
        return startOverEpgPoll(options, media).pipe(
          map(x => {
            return { startOverEpgResult: x };
          })
        );
      }
      return liveEpgPoll(media.metadata.guid!, options, includeStartOverInformation).pipe(
        map(x => ({ liveEpgResult: x }))
      );
    }),
    // Prevent duplicate requests in case of multiple subscribers
    share()
  );
}

function liveEpgPoll(
  guid: string,
  options: UseTrackingProps['epgOptions'],
  includeStartOverInformation: boolean
) {
  return defer(() =>
    query<PlayUnifiedLiveEpgQuery>(options, guid!, includeStartOverInformation)
  ).pipe(
    catchError((_: unknown) => EMPTY), // retry
    repeatWhen(completed$ => completed$.pipe(delay(5 * minute))),
    // Stop the polling if the entity type is not a broadcast
    takeWhile(data => data?.entity?.type === 'BROADCAST')
  );
}

export type FindCurrentEpgEntryProgramNode = {
  start: number;
  stop: number;
  title: string;
  transmissionId: string | null;
  startOverAvailable: boolean;
  type: 'program' | 'subProgram';
  entityId?: string | null;
  imageUrl?: string | null;
};

/** A function that finds the current EPG entry from a result of PlayUnifiedLiveTrackingQuery and a timestamp. */
export function findCurrentEpgEntryProgram(data: PlayUnifiedLiveEpgQuery, timestamp: number) {
  if (data.entity == null || !('epgEntries' in data.entity)) {
    return null;
  }
  const { nodes } = data.entity.epgEntries!;

  const matches = [] as FindCurrentEpgEntryProgramNode[];

  const node = nodes!.find(
    entry => (entry!.start ?? 0) <= timestamp && (entry!.stop ?? 0) >= timestamp
  );
  if (node) {
    matches.push({
      start: node.start ?? 0,
      stop: node.stop ?? 0,
      title: node.title ?? '',
      transmissionId: node.transmissionId,
      type: 'program',
      startOverAvailable: node.startOverAvailable ?? false,
      entityId: node.entityId,
      imageUrl: node.imageUrl,
    });
  }

  return matches.length > 0 ? matches[0] : null;
}
